import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ArticleItem } from '@modules/shopping/models/article-item.model';
import {
  HsButtonModule,
  HsDividerModule,
  HsFormFieldModule,
  HsIconModule,
  HsInputModule,
  HsSelectModule,
  HsSelectOption,
} from '@hsetu/ngui-components';
import { KfmAppItemType } from '@modules/shopping/enums/item-type.enum';
import { KfmAppItemApplyToType } from '@modules/shopping/enums/item-apply-to-type.enum';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ArticleItemService } from '@modules/shopping/services/article-item.service';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ICON_DELETE,
  ICON_SHOPPING_CART,
} from '@shared/ui/icons/icon-constants';
import { HsLayerModule } from '@hsetu/ngui-resources';
import {
  APPLY_TO_LABEL,
  CANCEL_LABEL,
  END_POSITION_LABEL,
  ITEM_LABEL,
  ITEM_TYPE_LABEL,
  OK_LABEL,
  REFERENCE_VALUE_LABEL,
  SELECTED_ITEMS_LABEL,
  SHORT_TEXT_LABEL,
  START_POSITION_LABEL,
  TOTAL_LABEL,
  UNIT_LABEL,
} from '@modules/shopping/i18n/label-text-constants';

@Component({
  selector: 'kfm-app-new-discount-surcharge-item-dialog',
  imports: [
    FormsModule,
    CommonModule,
    HsSelectModule,
    HsFormFieldModule,
    HsDividerModule,
    HsIconModule,
    HsInputModule,
    HsLayerModule,
    HsButtonModule,
  ],
  templateUrl: './kfm-discount-surcharge-article-dialog.component.html',
})
export class KfmAppNewDiscountSurchargeItemDialogComponent {
  private readonly _cart = signal<ArticleItem[] | undefined>(undefined);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _itemStore = inject(ArticleItemService);
  private readonly _data = inject<{
    item: ArticleItem | undefined;
    cart: ArticleItem[] | undefined;
    showPrice: boolean;
  }>(DIALOG_DATA);

  // Current selected article type option
  private readonly _selectedArticleType = signal<number>(2);

  // Current selected applyTo option
  private readonly _selectedApplyTo = signal<number>(0);

  // Current selected startIndexItem for ApplyTo 'From-To' option
  private readonly _selectedStartIndexItem = signal<string>('');
  private readonly _startItemOptions = signal<ArticleItem[]>([]);

  // Current selected endIndexItem for ApplyTo 'From-To' option
  private readonly _selectedEndIndexItem = signal<string>('');
  private readonly _endItemOptions = signal<ArticleItem[]>([]);

  // Current filtered cart for ApplyTo 'Custom' option
  private readonly _customItemOptions = signal<ArticleItem[]>([]);

  protected readonly itemTypeLabel = ITEM_TYPE_LABEL;
  protected readonly shortTextLabel = SHORT_TEXT_LABEL;
  protected readonly unitLabel = UNIT_LABEL;
  protected readonly applyToLabel = APPLY_TO_LABEL;
  protected readonly okLabel = OK_LABEL;
  protected readonly cancelLabel = CANCEL_LABEL;
  protected readonly referenceValueLabel = REFERENCE_VALUE_LABEL;
  protected readonly totalLabel = TOTAL_LABEL;
  protected readonly selectedItemsLabel = SELECTED_ITEMS_LABEL;
  protected readonly startPositionLabel = START_POSITION_LABEL;
  protected readonly endPositionLabel = END_POSITION_LABEL;
  protected readonly itemLabel = ITEM_LABEL;

  // Current selected items in the cart
  protected readonly selectedItems = signal<ArticleItem[]>([]);

  // Current selected discount or surcharge price value
  protected readonly selectedPrice = signal<number>(0);

  // Current selected currency option
  protected readonly selectedCurrency = signal<string>('€');

  protected readonly dialogRef = inject<DialogRef<ArticleItem>>(
    DialogRef<ArticleItem>,
  );

  // Currently selected article
  protected selectedArticle: ArticleItem = null;

  // Article type options
  protected get articleTypeOptions(): HsSelectOption<string>[] {
    return ['Rabatt', 'Zuschlag'].map((v) => ({ value: v, label: v }));
  }

  // Currency options
  protected get currencyOptions(): HsSelectOption<string>[] {
    return ['€', '%'].map((v) => ({ value: v, label: v }));
  }

  // Apply to options
  protected get applyToOptions(): string[] {
    return ['Gesamter Warenkorb', 'Von-Bis', 'Benutzerdefiniert'];
  }

  protected get applyToSelectOptions(): HsSelectOption<string>[] {
    return this.applyToOptions.map((v) => ({ value: v, label: v }));
  }

  // Icons cart
  protected readonly cartIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_SHOPPING_CART);

  // Icons trash
  protected readonly trashIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_DELETE);

  protected readonly selectedArticleType = computed(() => {
    const type = this._selectedArticleType();

    if (type === KfmAppItemType.Discount) return 'Rabatt';
    else return 'Zuschlag';
  });

  protected readonly price = computed(() => {
    const currency = this.selectedCurrency();
    const price = this.selectedPrice();

    if (currency === '€') {
      return this.selectedArticle ? (this.selectedArticle.priceTotal?.netTotal ?? 0) : price;
    } else {
      let netto = this.itemsSumNetto();

      if (this.selectedArticle.percentage) {
        netto = netto * (this.selectedArticle.percentage / 100.0);
        return Math.round((netto + Number.EPSILON) * 100) / 100;
      } else {
        // caluclate the absolute discount or surcharge value as percentage
        const unitPrice = this.selectedArticle.priceTotal?.netTotal ?? 0;
        return netto == 0 && unitPrice == 0
          ? 0
          : Math.round(((unitPrice / netto) * 100 + Number.EPSILON) * 100) /
              100;
      }
    }
  });

  protected readonly applyTo = computed(() => {
    const applyTo = this._selectedApplyTo();

    if (applyTo === KfmAppItemApplyToType.Cart) {
      return this.applyToOptions[0];
    } else if (applyTo === KfmAppItemApplyToType.FromTo) {
      return this.applyToOptions[1];
    } else {
      return this.applyToOptions[2];
    }
  });

  protected readonly startItemOptions = computed((): HsSelectOption<string>[] => {
    const cart = this._startItemOptions();

    if (!cart) {
      return [];
    }

    return cart.map((i) => {
      const name = this.getArticleName(i);
      return { value: name, label: name };
    });
  });

  protected readonly endItemOptions = computed((): HsSelectOption<string>[] => {
    const cart = this._endItemOptions();

    if (!cart) {
      return [];
    }

    return cart.map((i) => {
      const name = this.getArticleName(i);
      return { value: name, label: name };
    });
  });

  // Names of the filtered cart items for selection
  protected readonly customItemOptions = computed((): HsSelectOption<string>[] => {
    const items = this._customItemOptions();
    return items.map((i) => {
      const name = this.getArticleName(i);
      return { value: name, label: name };
    });
  });

  // Calculate the whole price of the cart excluding mwst
  protected readonly itemsSumNetto = computed((): number => {
    const items = this.selectedItems();
    let sum = 0;

    items.forEach((item: ArticleItem) => {
      sum += Number(item.amount * (item.priceTotal?.netTotal ?? 0));
    });

    return Math.round((sum + Number.EPSILON) * 100) / 100;
  });

  // Calculate the whole price of the cart including mwst
  protected readonly itemsSumFull = computed((): number => {
    const price = this.price();

    if (this.selectedArticleType() === this.articleTypeOptions[0].value) {
      return (
        Math.round((this.itemsSumNetto() - price + Number.EPSILON) * 100) / 100
      );
    } else {
      return (
        Math.round((this.itemsSumNetto() + price + Number.EPSILON) * 100) / 100
      );
    }
  });

  //#endregion ----

  protected readonly endIndexItemDisabled = computed((): boolean => {
    const cart = this._endItemOptions();

    return (
      !this.selectedArticle ||
      !this.selectedArticle.itemIds ||
      this.selectedArticle.itemIds.length === 0 ||
      !cart ||
      cart.length === 0
    );
  });

  protected readonly customItemSelectionDisabled = computed((): boolean => {
    const cart = this._cart();
    const selectedItems = this.selectedItems();

    return cart.length == selectedItems.length;
  });

  // Start index of items in the range selection option
  protected readonly startIndexItem = computed<string>(() => {
    const id = this._selectedStartIndexItem();

    // get the id from filtered cart
    return this.getItemById(id);
  });

  // End index of items in the range selection option
  public readonly endIndexItem = computed<string>(() => {
    const id = this._selectedEndIndexItem();

    // get the id from filtered cart
    return this.getItemById(id);
  });

  public readonly valid = computed((): boolean => {
    const selectedItems = this.selectedItems();
    const price = this.price();

    return selectedItems.length > 0 && price > 0;
  });

  protected get showPrices() {
    return this._data.showPrice;
  }

  protected get title() {
    return this.selectedArticle ? this.selectedArticle.infoDesc : '';
  }

  constructor() {
    let cart = this._data.cart;

    if (cart) {
      // remove all text, discount and surcharge items
      cart = cart.filter((i) => !i.type || i.type === KfmAppItemType.Item);
      cart.sort(this.compare);
    }

    if (this._data.item) {
      this.selectedArticle = this._data.item;
    } else {
      this.selectedArticle = this._itemStore.getTemplate();
      this.selectedArticle.applyTo = KfmAppItemApplyToType.Cart;
      this.selectedArticle.type = KfmAppItemType.Discount;
      this.selectedArticle.itemIds = cart ? cart.map((i) => i.uId) : undefined;
    }

    if (!this.selectedArticle.itemIds) {
      this.selectedArticle.itemIds = [];
    }

    this._selectedArticleType.set(this.selectedArticle.type);
    this._selectedApplyTo.set(this.selectedArticle.applyTo);

    if (
      !this.selectedArticle.percentage ||
      this.selectedArticle.percentage === 0
    ) {
      this.selectedCurrency.set('€');
      this.selectedPrice.set(this.selectedArticle.priceTotal?.netTotal ?? 0);
    } else {
      this.selectedCurrency.set('%');
      this.selectedPrice.set(this.selectedArticle.percentage ?? 0);
    }

    this._cart.set(cart);

    this.configureSignals();
  }

  protected onArticleTypeChanged(event: HsSelectOption<string>) {
    if (this.selectedArticle) {
      this.selectedArticle.type =
        event.value === 'Rabatt' ? KfmAppItemType.Discount : KfmAppItemType.Surcharge;
      this._selectedArticleType.set(this.selectedArticle.type);
    }
  }

  protected onTitleChanged(newTitle: string): void {
    if (this.selectedArticle) {
      this.selectedArticle.infoDesc = newTitle.trim();
    }
  }

  protected onApplyToChanged(event: HsSelectOption<string>) {
    this.selectedArticle.itemIds = [];

    if (event.value === this.applyToOptions[0]) {
      // whole cart
      this.selectedArticle.applyTo = KfmAppItemApplyToType.Cart;

      if (this._cart) {
        this.selectedArticle.itemIds = this._cart().map((i) => i.uId);
      }
    } else if (event.value === this.applyToOptions[1]) {
      // item range
      this.selectedArticle.applyTo = KfmAppItemApplyToType.FromTo;
    } else {
      // Custom items
      this.selectedArticle.applyTo = KfmAppItemApplyToType.Custom;
    }

    this._selectedApplyTo.set(this.selectedArticle.applyTo);

    this.configureSignals();
  }

  protected onItemRangeChanged(event: HsSelectOption<string>, firstItem: boolean) {
    const cart = this._cart() ?? [];

    const index = cart
      .map((i) => {
        return this.getArticleName(i);
      })
      .findIndex((i) => i === event.value);

    if (firstItem) {
      // Reset end index item
      this._selectedEndIndexItem.set('');
      this.selectedArticle.itemIds = [cart[index].uId];
      this._selectedStartIndexItem.set(this.getArticleName(cart[index]));

      if (index + 1 < cart.length) {
        // Update filtered cart to only show items after the selected start index
        this._endItemOptions.update(() => cart.slice(index + 1));
      } else {
        this._endItemOptions.update(() => []);
      }

      this.selectedItems.set([cart[index]]);
    } else {
      // get all items of the cart that are bewtween itemIds[0] and selected end index
      const start = cart.findIndex(
        (i) => i.uId === this.selectedArticle.itemIds[0],
      );

      if (start + 1 < cart.length) {
        // Update filtered cart to only show items after the selected start index
        this._endItemOptions.update(() => cart.slice(start + 1));
      } else {
        this._endItemOptions.update(() => []);
      }

      const end = index;

      // Add all items in selected range to the option
      this.selectedArticle.itemIds = cart
        .slice(start, end + 1)
        .map((i) => i.uId);

      this.selectedItems.set(cart.slice(start, end + 1));
      this._selectedEndIndexItem.set(this.getArticleName(cart[index]));
    }
  }

  protected onItemCustomChanged(event: HsSelectOption<string>) {
    const cart = this._customItemOptions();

    const index = cart.findIndex((i) => this.getArticleName(i) === event.value);
    const itemId = cart[index].uId;

    if (
      !this.selectedArticle.itemIds ||
      this.selectedArticle.itemIds.length === 0
    ) {
      // There was no item selected yet
      this.selectedArticle.itemIds = [itemId];
      this.selectedItems.update(() => [cart[index]]);
    } else {
      const index = this.selectedArticle.itemIds.findIndex(
        (i: string) => i === itemId,
      );

      if (index === -1) {
        this.selectedArticle.itemIds.push(itemId);
        this.selectedItems.update((old) => [...old, cart[index]]);
      }
    }

    // Remove the selected item from the custom item options
    this._customItemOptions.set(cart.filter((i) => i.uId !== itemId));
  }

  protected onRemoveItemClicked(itemId: string) {
    if (this.selectedArticle && this.selectedArticle.itemIds) {
      const index = this.selectedArticle.itemIds.findIndex(
        (i: string) => i === itemId,
      );

      if (index > -1) {
        this.selectedArticle.itemIds.splice(index, 1);
      }
    }

    // add item to custom item options
    const cart = this._cart() ?? [];
    const item = cart.find((i) => i.uId === itemId);
    if (item) {
      this._customItemOptions.update((old) =>
        [...old, item].sort(this.compare),
      );
    }

    // remove item from selected items
    const selectedItems = this.selectedItems();
    const selectedIndex = selectedItems.findIndex((i) => i.uId === itemId);
    if (selectedIndex > -1) {
      selectedItems.splice(selectedIndex, 1);
      this.selectedItems.set([...selectedItems]);
    }
  }

  protected onCurrencyChanged(event: HsSelectOption<string>) {
    if (event.value === '€') {
      if (!this.selectedArticle.priceTotal) this.selectedArticle.priceTotal = {};
      this.selectedArticle.priceTotal.netTotal = 0;
      this.selectedArticle.percentage = undefined;
    } else {
      if (!this.selectedArticle.priceTotal) this.selectedArticle.priceTotal = {};
      this.selectedArticle.priceTotal.netTotal = 0;
      this.selectedArticle.percentage = 0;
    }

    this.selectedCurrency.set(event.value);
    this.selectedPrice.set(0);
  }

  protected onPriceChanged(event: number) {
    if (!this.selectedArticle) {
      return;
    }

    const currency = this.selectedCurrency();

    if (currency === '€') {
      if (!this.selectedArticle.priceTotal) this.selectedArticle.priceTotal = {};
      this.selectedArticle.priceTotal.netTotal = Number(event * 1);
      this.selectedArticle.percentage = undefined;
    } else {
      if (!this.selectedArticle.priceTotal) this.selectedArticle.priceTotal = {};
      this.selectedArticle.priceTotal.netTotal = 0;
      this.selectedArticle.percentage = Number(event * 1);
    }

    this.selectedPrice.set(Number(event * 1));
  }

  protected getItemHeader(item: ArticleItem): string {
    return this.getArticleName(item);
  }

  protected getItemSubHeader(item: ArticleItem): string {
    let desc =
      !!item.manufacturerLabel && item.manufacturerLabel.trim().length > 0
        ? "Hersteller: <span class='font-normal'>" +
          item.manufacturerLabel +
          '</span>'
        : '';

    const unitPrice: string = this.showPrices
      ? (item.priceTotal?.netTotal ?? 0).toString()
      : '***';

    desc += (item.priceTotal?.netTotal ?? 0)
      ? desc.trim().length > 0
        ? ", Preis: <span class='font-normal'>" + unitPrice + '€</span>'
        : "Preis: <span class='font-normal'>" + unitPrice + '€</span>'
      : '';

    let amount = item.amount ? item.amount.toString() : '';
    amount +=
      !!item.unit && item.unit.trim().length > 0
        ? amount.length > 0
          ? ' ' + item.unit
          : item.unit
        : '';

    desc += item.amount
      ? desc.trim().length > 0
        ? ", Anzahl: <span class='font-normal'>" + amount + '</span>'
        : "Anzahl: <span class='font-normal'>" + amount + '</span>'
      : '';

    return desc;
  }

  private compare(a: ArticleItem, b: ArticleItem) {
    const sortNumA = a.sortNum ?? Number.MAX_VALUE;
    const sortNumB = b.sortNum ?? Number.MAX_VALUE;
    return sortNumA - sortNumB;
  }

  private getItemById(itemId: string): string {
    // get the id from filtered cart
    const cart = this._cart();
    const item = cart.find((i) => i.uId === itemId);

    if (!item) {
      return '';
    }

    return this.getArticleName(item);
  }

  private getArticleName(item: ArticleItem): string {
    return item.articleNumber && item.articleNumber.trim().length > 0
      ? '[' + item.articleNumber + '] ' + item.infoDesc
      : item.infoDesc;
  }

  private configureSignals() {
    const cart = this._cart() ?? [];

    if (this.selectedArticle.applyTo === KfmAppItemApplyToType.Cart) {
      this._selectedStartIndexItem.set('');
      this._selectedEndIndexItem.set('');
      this._startItemOptions.set([...cart]);
      this._endItemOptions.set([...cart]);
      this._customItemOptions.set([...cart]);

      this.selectedArticle.itemIds = cart ? cart.map((i) => i.uId) : undefined;
      this.selectedItems.set([...cart]);
    } else if (this.selectedArticle.applyTo === KfmAppItemApplyToType.FromTo) {
      // set start and end index items
      if (
        this.selectedArticle.itemIds &&
        this.selectedArticle.itemIds.length > 0
      ) {
        const firstItemId = this.selectedArticle.itemIds[0];
        const lastItemId =
          this.selectedArticle.itemIds[this.selectedArticle.itemIds.length - 1];
        this._selectedStartIndexItem.set(firstItemId);

        if (firstItemId !== lastItemId)
          this._selectedEndIndexItem.set(lastItemId);

        this._startItemOptions.set([]);
        this._endItemOptions.set([]);

        const selectedItems = cart.filter((i) =>
          this.selectedArticle.itemIds.includes(i.uId),
        );
        this.selectedItems.set(selectedItems);
      } else {
        this._selectedStartIndexItem.set('');
        this._selectedEndIndexItem.set('');
        this._startItemOptions.update(() => [...cart]);
        this._endItemOptions.update(() => [...cart]);
        this.selectedItems.set([]);
      }

      this._customItemOptions.set([...cart]);
    } else {
      this._selectedStartIndexItem.set('');
      this._selectedEndIndexItem.set('');
      this._startItemOptions.set([...cart]);
      this._endItemOptions.set([...cart]);

      const selectedItems = cart.filter((i) =>
        this.selectedArticle.itemIds.includes(i.uId),
      );
      this.selectedItems.set(selectedItems);

      // filter cart items without selected items
      this._customItemOptions.set(
        cart.filter((i) => !this.selectedArticle.itemIds.includes(i.uId)),
      );
    }
  }
}
